/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-02 16:48:45
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';

import { DASHBOARD_LAYOUT } from 'src/constants/dashboard-layout';
import { BFCM2022Constants, BFCM2023Constants, Xmas22Constants } from 'src/constants/campaigns';
import { allowToSubscribeBFCM20Code } from './subscription-helper';
import plansOld from 'src/constants/plans-old.json';
import {
  DASHBOARD_LAYOUT_FIRST_COLUMN,
  DASHBOARD_LAYOUT_SECOND_COLUMN,
  DASHBOARD_LAYOUT_TOP
} from '../constants/dashboard-layout';
/**
 * Determine display blocks
 *
 * @param  {object} dashboardBlocks list of display blocks from tenant app
 *
 * @return {array}                  list of blocks will be displayed on dashboard
 */
export const getDisplayBlocks = (dashboardBlocks: Object, tenantApp: Object) => {
  let layouts = [];

  // Avoid display default blocks before getting blocks successfully from API
  if (_.isEmpty(dashboardBlocks)) return [];

  DASHBOARD_LAYOUT.map(layout => {
    let sections = [];

    layout.map(section => {
      // Always has block Notifications
      if (
        section === 'notifications' ||
        section === 'topNotifications' ||
        dashboardBlocks[section]
      ) {
        sections.push(section);
      }
      // Experiment of Growth team
      // Replace themeSetupStatus by expReviewRating
      if (section === 'themeSetupStatus' && dashboardBlocks['expReviewRating']) {
        sections.push('expReviewRating');
      }
      return '';
    });

    if (!_.isEmpty(sections)) {
      layouts.push(sections);
    }
    return '';
  });

  return layouts;
};

export const getDashboardBlocksTop = dashboardBlocks => {
  let layouts = [];

  // Avoid display default blocks before getting blocks successfully from API
  if (_.isEmpty(dashboardBlocks)) return [];

  DASHBOARD_LAYOUT_TOP.map(layout => {
    let sections = [];

    layout.map(section => {
      if (dashboardBlocks[section]) {
        sections.push(section);
      }
      return '';
    });

    if (!_.isEmpty(sections)) {
      layouts.push(sections);
    }
    return '';
  });

  return layouts;
};
/**
 * Determine display blocks at first column
 *
 * @param  {object} dashboardBlocks list of display blocks from tenant app
 *
 * @return {array}                  list of blocks will be displayed on dashboard
 */
export const getDisplayBlocksFirstColumn = (dashboardBlocks: Object, tenantApp: Object) => {
  let layouts = [];

  // Avoid display default blocks before getting blocks successfully from API
  if (_.isEmpty(dashboardBlocks)) return [];

  DASHBOARD_LAYOUT_FIRST_COLUMN.map(layout => {
    let sections = [];

    layout.map(section => {
      if (dashboardBlocks[section] || section === 'notifications') {
        sections.push(section);
      }
      return '';
    });

    if (!_.isEmpty(sections)) {
      layouts.push(sections);
    }
    return '';
  });

  return layouts;
};

/**
 * Determine display blocks at second column
 *
 * @param  {object} dashboardBlocks list of display blocks from tenant app
 *
 * @return {array}                  list of blocks will be displayed on dashboard
 */
export const getDisplayBlocksSecondColumn = (dashboardBlocks: Object, tenantApp: Object) => {
  let layouts = [];

  // Avoid display default blocks before getting blocks successfully from API
  if (_.isEmpty(dashboardBlocks)) return [];

  DASHBOARD_LAYOUT_SECOND_COLUMN.map(layout => {
    let sections = [];

    layout.map(section => {
      if (dashboardBlocks[section]) sections.push(section);
      return '';
    });

    if (!_.isEmpty(sections)) {
      layouts.push(sections);
    }
    return '';
  });

  return layouts;
};

/**
 * Check to update layout block with requirement from BFCM 2020
 *
 * @param  {object} dashboardBlocks list of display blocks from tenant app
 *
 * @return {array}                  list of blocks will be displayed on dashboard
 */
export const updateBlocksWithBFCM2020 = (layouts: Array<Object>, tenantApp: Object) => {
  if (allowToSubscribeBFCM20Code(tenantApp)) {
    const index = _.findIndex(layouts, layout => {
      return layout[0].includes('welcome');
    });
    if (index !== -1) {
      let planId = _.get(tenantApp, 'selectedPlan', '');
      planId = planId ? planId.replace('_discount', '').toLowerCase() : planId;
      const plan = _.get(plansOld, planId, {});

      if (plan.products && plan.products <= 5000) {
        // low plan
        layouts[index] = ['welcomeOldLowPlus'];
      } else {
        // high plan
        layouts[index] = ['welcomeOldHighPlus'];
      }
    }
  }
  return layouts;
};

/**
 * check localStorage to check if user close before,...
 * @param {*} tenantApp
 * @returns
 */
export const allowToShowBFCM22 = tenantApp => {
  const current = moment();
  const startBFCM2022 = moment(BFCM2022Constants.StartDate);
  const endBFCM2022 = moment(BFCM2022Constants.EndDate);

  try {
    const isDoneBFCM2022 = localStorage.getItem(`${tenantApp.tenantId}-doneSpecialDiscount`);

    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
    if (
      current.isBefore(endBFCM2022) &&
      current.isAfter(startBFCM2022) &&
      tenantApp.notChargedBefore &&
      _.isEmpty(selectedPlan) &&
      !isDoneBFCM2022
    ) {
      return true;
    }
  } catch (err) {}

  return false;
};

export const BFCM23_CODE = {
  PLAN: 'BFCM23',
  ANALYTIC365: 'ANALYTIC23'
};

export const getPackageTypeByPlanId = selectedPlan => {
  if (selectedPlan === '') {
    return 'trial';
  } else if (selectedPlan.startsWith('basic_')) {
    return 'basic';
  } else if (selectedPlan.startsWith('essential_')) {
    return 'essential';
  } else if (selectedPlan.startsWith('professional_')) {
    return 'professional';
  } else {
    return 'basic';
  }
};

/**
 * @param {*} tenantApp
 * @returns
 */
export const allowToShowBFCM23 = tenantApp => {
  const { selectedPlan = '', codeBFCMApplied, settings } = tenantApp;
  const addonAnalytic365 = _.get(settings, 'analyticV2.settings.analytics-365-days', false);

  const plan = getPackageTypeByPlanId(selectedPlan.replace('_discount', ''));

  let startDateUtcUat = null;
  let endDateUtcUat = null;
  try {
    startDateUtcUat = localStorage.getItem('bfcm23-start'); //2023-09-19T01:00:00+00:00
    endDateUtcUat = localStorage.getItem('bfcm23-end'); //2023-09-19T03:00:00+00:00
  } catch (error) {}

  const current = moment();
  const startBFCM2023 = moment(startDateUtcUat || BFCM2023Constants.StartDate);
  const endBFCM2023 = moment(endDateUtcUat || BFCM2023Constants.EndDate);
  let result = {
    allow: false
  };

  // TODO
  //? Check if tenant has applied the code or not
  if (
    codeBFCMApplied?.includes(BFCM23_CODE.PLAN) ||
    codeBFCMApplied?.includes(BFCM23_CODE.ANALYTIC365)
  ) {
    return result;
  }

  try {
    if (current.isBefore(endBFCM2023) && current.isAfter(startBFCM2023)) {
      if (plan === 'trial') {
        result = {
          allow: true,
          type: 'trial',
          packagesHighlight: ['basic', 'essential', 'professional']
        };
      }
      if (plan === 'basic') {
        result = {
          allow: true,
          type: 'upgrade',
          packagesHighlight: ['essential', 'professional']
        };
      }
      if (plan === 'essential') {
        result = {
          allow: true,
          type: 'upgrade',
          packagesHighlight: ['professional']
        };
      }
      if (plan === 'professional' && !addonAnalytic365) {
        result = {
          allow: true,
          type: 'professional365',
          packagesHighlight: []
        };
      }
    }
  } catch (err) {}
  return result;
};

/**
 * Set dismiss time for BFCM2022 modal
 * @param {*} tenantId
 */
export const checkAndSetBFCM2022 = tenantId => {
  try {
    const current = moment().endOf('day').toISOString();
    const getBFCM2022LocalStorage = localStorage.getItem(`${tenantId}-isNotShowBFCM2022Again1Day`);

    // Check at the end of date that customer visit the app
    if (getBFCM2022LocalStorage) {
      if (getBFCM2022LocalStorage === current) {
        localStorage.removeItem(`${tenantId}-dismissBFCM2022`);
      } else {
        localStorage.removeItem(`${tenantId}-isNotShowBFCM2022Again1Day`);
      }
    } else {
      localStorage.removeItem(`${tenantId}-dismissBFCM2022`);
    }
  } catch (error) {}
};

/**
 * Set dismiss time for Special discount modal
 * @param {*} tenantId
 */
export const checkAndSetSpecialDiscount = tenantId => {
  try {
    const current = moment().endOf('day').toISOString();
    const getSpecialDiscountLocalStorage = localStorage.getItem(
      `${tenantId}-countShowPopupBfcmDate`
    );

    // Check at the end of date that customer visit the app
    if (getSpecialDiscountLocalStorage) {
      if (getSpecialDiscountLocalStorage === current) {
        localStorage.removeItem(`${tenantId}-dismissBFCM23`);
      } else {
        localStorage.removeItem(`${tenantId}-countShowPopupBfcmDate`);
      }
    } else {
      localStorage.removeItem(`${tenantId}-dismissBFCM23`);
    }
  } catch (err) {}
};

/**
 * check localStorage to check if user close before,...
 * @param {*} tenantApp
 * @returns
 */
export const allowToShowSpecialDiscount = (tenantApp, startDate, endDate) => {
  try {
    const current = moment();
    const start = moment(startDate);
    const end = moment(endDate);

    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');

    if (
      current.isBefore(end) &&
      current.isAfter(start) &&
      tenantApp.notChargedBefore &&
      _.isEmpty(selectedPlan)
    ) {
      return true;
    }
    return false;
  } catch (err) {}
};

/**
 * Check days left special discount from current date
 * @returns {number} days
 */
export const checkDateCountDown = endDate => {
  const current = moment().startOf('day');
  const end = moment(endDate).startOf('day');
  const duration = moment.duration(end.diff(current));
  const days = duration.asDays();
  return days;
};

/**
 * Check to update layout block with requirement from BFCM 2020
 *
 * @param  {object} dashboardBlocks list of display blocks from tenant app
 *
 * @return {array}                  list of blocks will be displayed on dashboard
 */
export const updateBlocksWithSpecialDiscount = (layouts: Array<Object>, tenantApp: Object) => {
  let _layouts = layouts;
  if (allowToShowSpecialDiscount(tenantApp, Xmas22Constants.startDate, Xmas22Constants.endDate)) {
    const INDEX_BFCM = 2;
    _layouts = layouts.reduce((result, item, index) => {
      if (index === INDEX_BFCM) {
        return [...result, ['specialDiscount'], item];
      } else {
        return [...result, item];
      }
    }, []);
  }
  return _layouts;
};

/**
 * Get dismissed notifications from localstorage
 *
 * @param  {string} shop: string        shop name
 *
 * @return {array}                      list of dismissed notification
 */
export const getDismissedNotifications = (shop: string) => {
  let dismissedNotifications = '';
  try {
    dismissedNotifications = localStorage.getItem(`${shop}-dismissedNotifications`);
  } catch (error) {}

  return dismissedNotifications ? JSON.parse(dismissedNotifications) : [];
};

/**
 * Dismiss notification
 * Save notification id to localstorage
 *
 * @param  {string} notificationId: string        notification id
 * @param  {string} shop:           string        shop name
 */
export const dismissNotification = (notificationId: string, shop: string) => {
  // Get list of dismissed notification from local storage
  let dismissedNotifications = getDismissedNotifications(shop);

  // If including, skip
  if (_.includes(dismissedNotifications, notificationId)) return;

  // Write notificationId to localstorage
  dismissedNotifications.push(notificationId);
  try {
    localStorage.setItem(`${shop}-dismissedNotifications`, JSON.stringify(dismissedNotifications));
  } catch (error) {}
};

/**
 * Get list of display notification
 *
 * @param  {array} notifications: Array<Object> list of notification
 * @param  {string} shop:          string       shop name
 *
 * @return {array}                              list of display notification that excludes dismissed notification
 */
export const getDisplayNotifications = (notifications: Array<Object>, shop: string) => {
  // Get list of dismissed notifications from localstorage
  const dismissedNotifications = getDismissedNotifications(shop);

  // Hide Post-launch notification for specific stores
  const excludedStore = ['theshopcom.myshopify.com'];

  // Filter
  let displayNotifications = _.filter(notifications, notification => {
    const { title } = notification;
    return (
      !_.includes(dismissedNotifications, notification.id) &&
      (title !== 'In case you missed it' ||
        (title === 'In case you missed it' && !_.includes(excludedStore, shop)))
    ); // TODO: Compare by id instead of text
  });

  return displayNotifications;
};
